<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import {
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  inboxId: { type: Number, default: null },
  template: { type: Object, default: null },
});

const emit = defineEmits(['saved', 'close']);

const store = useStore();
const { t } = useI18n();

const isEditMode = computed(() => !!props.template);

// ESC key closes the modal
const handleKeydown = e => {
  if (e.code === 'Escape') {
    e.stopPropagation();
    emit('close');
  }
};
onMounted(() => document.addEventListener('keydown', handleKeydown));
onBeforeUnmount(() => document.removeEventListener('keydown', handleKeydown));

const loading = ref(false);
const name = ref('');
const language = ref('pt_BR');
const category = ref('UTILITY');
const headerType = ref('NONE');
const headerText = ref('');
const bodyText = ref('');
const footerText = ref('');
const buttons = ref([]);

// NOOVI: estado do header de mídia (whatsapp-template-media)
const headerHandle = ref(''); // handle h:... retornado pela Meta
const headerMediaName = ref(''); // nome do arquivo enviado (UI)
const headerMediaPreviewUrl = ref(''); // object URL p/ preview de imagem
const uploadingMedia = ref(false);
const mediaError = ref('');
const mediaInputRef = ref(null);

// NOOVI: estado da geração por IA (whatsapp-template-ai)
const aiPromptOpen = ref(false);
const aiPrompt = ref('');
const aiGenerating = ref(false);

// Task #10: parameter_format toggle
const parameterFormat = ref('POSITIONAL');

// Task #7: example inputs for variables
const bodyExamples = ref([]);
const headerExamples = ref([]);

// Task #12: named vars hint panel state
const namedVarsHintOpen = ref(false);

// Ref for body textarea (for cursor insertion)
const bodyTextareaRef = ref(null);

// Populate form when editing
if (props.template) {
  const tmpl = props.template;
  name.value = tmpl.name || '';
  language.value = tmpl.language || 'pt_BR';
  category.value = tmpl.category || 'UTILITY';
  // Recupera o formato de parâmetro do template existente. Sem isto, editar um
  // template NAMED reconstruía os componentes em formato POSITIONAL (default),
  // gerando exemplos inconsistentes no update enviado à Meta.
  parameterFormat.value =
    tmpl.parameter_format === 'NAMED' ? 'NAMED' : 'POSITIONAL';
  const header = tmpl.components?.find(c => c.type === 'HEADER');
  if (header) {
    headerType.value = header.format || 'NONE';
    headerText.value = header.text || '';
    // NOOVI: recupera o handle de um header de mídia já criado para preservá-lo
    // no update (Meta exige reenviar o example) (whatsapp-template-media)
    const existingHandle = header.example?.header_handle?.[0];
    if (existingHandle) {
      headerHandle.value = existingHandle;
      headerMediaName.value = headerType.value;
    }
  }
  const body = tmpl.components?.find(c => c.type === 'BODY');
  bodyText.value = body?.text || '';
  const footer = tmpl.components?.find(c => c.type === 'FOOTER');
  footerText.value = footer?.text || '';
  const btns = tmpl.components?.find(c => c.type === 'BUTTONS');
  if (btns?.buttons) {
    buttons.value = btns.buttons.map(b => ({
      type: b.type,
      text: b.text || '',
      url: b.url || '',
      phone: b.phone_number || '',
    }));
  }
}

const LANGUAGES = [
  { value: 'pt_BR', label: 'Português (BR)' },
  { value: 'en_US', label: 'English (US)' },
  { value: 'es', label: 'Español' },
  { value: 'es_AR', label: 'Español (AR)' },
];

const CATEGORIES = [
  { value: 'UTILITY', label: 'Utility' },
  { value: 'MARKETING', label: 'Marketing' },
  { value: 'AUTHENTICATION', label: 'Authentication' },
];

const HEADER_TYPES = [
  { value: 'NONE', label: 'None' },
  { value: 'TEXT', label: 'Text' },
  // NOOVI: headers de mídia habilitados — o upload via Meta Resumable Upload API
  // gera o header_handle exigido na criação (whatsapp-template-media).
  { value: 'IMAGE', label: 'Image' },
  { value: 'VIDEO', label: 'Video' },
  { value: 'DOCUMENT', label: 'Document' },
];

// NOOVI: mapeia tipo de header → accept do <input file> + categoria de mídia
// (whatsapp-template-media)
const MEDIA_HEADER_TYPES = ['IMAGE', 'VIDEO', 'DOCUMENT'];
const MEDIA_ACCEPT = {
  IMAGE: 'image/jpeg,image/png',
  VIDEO: 'video/mp4,video/3gpp',
  DOCUMENT:
    'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation',
};

// Live Preview background pattern — inline SVG data URI, fully self-contained.
// Replaces a dead external imgbb image (https://i.ibb.co/6ndV2XF/wa-pattern.png)
// that rendered a broken "image not found" placeholder tiled across the panel,
// making the preview look broken to users.
const PREVIEW_PATTERN = `url("data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20width='32'%20height='32'%3E%3Ccircle%20cx='4'%20cy='4'%20r='1.5'%20fill='%23000000'/%3E%3C/svg%3E")`;

// Task #12: canonical named variables list
const NAMED_VARS_GROUPS = [
  {
    labelKey: 'WHATSAPP_TEMPLATES.FORM.NAMED_VARS_HELP.GROUP_CONTACT',
    vars: [
      'contact_name',
      'contact_first_name',
      'contact_last_name',
      'contact_email',
      'contact_phone',
      'contact_id',
    ],
  },
  {
    labelKey: 'WHATSAPP_TEMPLATES.FORM.NAMED_VARS_HELP.GROUP_CONVERSATION',
    vars: ['conversation_id', 'inbox_id', 'inbox_name'],
  },
  {
    labelKey: 'WHATSAPP_TEMPLATES.FORM.NAMED_VARS_HELP.GROUP_AGENT',
    vars: ['agent_name', 'agent_first_name', 'agent_last_name', 'agent_email'],
  },
];

// ── Computed: variable extraction ────────────────────────────────────────────

const positionalVarRegex = /\{\{\d+\}\}/g;
const namedVarRegex = /\{\{([a-z][a-z0-9_]*)\}\}/g;

const extractBodyVars = computed(() => {
  if (parameterFormat.value === 'POSITIONAL') {
    const matches = bodyText.value.match(positionalVarRegex) || [];
    return matches;
  }
  const matches = [...bodyText.value.matchAll(namedVarRegex)];
  return matches.map(m => m[1]);
});

const extractHeaderVars = computed(() => {
  if (parameterFormat.value === 'POSITIONAL') {
    const matches = headerText.value.match(positionalVarRegex) || [];
    return matches;
  }
  const matches = [...headerText.value.matchAll(namedVarRegex)];
  return matches.map(m => m[1]);
});

// Task #10: detect invalid NAMED var names (uppercase, dots, hyphens)
const namedVarError = computed(() => {
  if (parameterFormat.value !== 'NAMED') return '';
  const invalidBodyVars = [
    ...(bodyText.value.matchAll(/\{\{([^}]+)\}\}/g) || []),
  ]
    .map(m => m[1])
    .filter(v => !/^[a-z][a-z0-9_]*$/.test(v));
  const invalidHeaderVars = [
    ...(headerText.value.matchAll(/\{\{([^}]+)\}\}/g) || []),
  ]
    .map(m => m[1])
    .filter(v => !/^[a-z][a-z0-9_]*$/.test(v));
  const all = [...new Set([...invalidBodyVars, ...invalidHeaderVars])];
  if (all.length === 0) return '';
  return t('WHATSAPP_TEMPLATES.FORM.NAMED_VAR_FORMAT_ERROR', {
    vars: all.map(v => `{{${v}}}`).join(', '),
  });
});

// ── Watchers: sync example arrays with var counts ────────────────────────────

watch(
  extractBodyVars,
  newVars => {
    const newLen = newVars.length;
    if (bodyExamples.value.length < newLen) {
      while (bodyExamples.value.length < newLen) bodyExamples.value.push('');
    } else if (bodyExamples.value.length > newLen) {
      bodyExamples.value.splice(newLen);
    }
  },
  { immediate: true }
);

watch(
  extractHeaderVars,
  newVars => {
    const newLen = newVars.length;
    if (headerExamples.value.length < newLen) {
      while (headerExamples.value.length < newLen)
        headerExamples.value.push('');
    } else if (headerExamples.value.length > newLen) {
      headerExamples.value.splice(newLen);
    }
  },
  { immediate: true }
);

// Task #8: when category changes away from AUTHENTICATION, remove COPY_CODE buttons
watch(category, newCat => {
  if (newCat !== 'AUTHENTICATION') {
    const hadCopyCode = buttons.value.some(b => b.type === 'COPY_CODE');
    if (hadCopyCode) {
      buttons.value = buttons.value.filter(b => b.type !== 'COPY_CODE');
    }
  }
});

// ── NOOVI: media header upload (whatsapp-template-media) ─────────────────────

const isMediaHeader = computed(() =>
  MEDIA_HEADER_TYPES.includes(headerType.value)
);

const mediaAccept = computed(() => MEDIA_ACCEPT[headerType.value] || '');

// Limpa o estado da mídia ao trocar o tipo de header (evita reusar handle de
// outro tipo). Revoga object URLs para não vazar memória.
watch(headerType, () => {
  if (headerMediaPreviewUrl.value) {
    URL.revokeObjectURL(headerMediaPreviewUrl.value);
  }
  headerHandle.value = '';
  headerMediaName.value = '';
  headerMediaPreviewUrl.value = '';
  mediaError.value = '';
});

const triggerMediaPicker = () => {
  if (mediaInputRef.value) mediaInputRef.value.click();
};

const handleMediaSelected = async event => {
  const file = event.target.files?.[0];
  if (!file) return;

  mediaError.value = '';
  uploadingMedia.value = true;
  try {
    const result = await store.dispatch(
      'whatsappTemplates/uploadTemplateMedia',
      {
        file,
        inboxId: props.inboxId,
      }
    );
    headerHandle.value = result.handle;
    headerMediaName.value = file.name;
    if (headerMediaPreviewUrl.value) {
      URL.revokeObjectURL(headerMediaPreviewUrl.value);
    }
    headerMediaPreviewUrl.value =
      headerType.value === 'IMAGE' ? URL.createObjectURL(file) : '';
  } catch (error) {
    mediaError.value =
      error?.response?.data?.error ||
      error?.message ||
      t('WHATSAPP_TEMPLATES.FORM.MEDIA.UPLOAD_ERROR');
    headerHandle.value = '';
    headerMediaName.value = '';
  } finally {
    uploadingMedia.value = false;
    // permite reenviar o mesmo arquivo
    if (mediaInputRef.value) mediaInputRef.value.value = '';
  }
};

onBeforeUnmount(() => {
  if (headerMediaPreviewUrl.value) {
    URL.revokeObjectURL(headerMediaPreviewUrl.value);
  }
});

// ── NOOVI: AI draft generation (whatsapp-template-ai) ────────────────────────

const handleGenerateAi = async () => {
  if (!aiPrompt.value.trim() || aiGenerating.value) return;

  aiGenerating.value = true;
  try {
    const draft = await store.dispatch('whatsappTemplates/generateTemplate', {
      prompt: aiPrompt.value,
      language: language.value,
      category: category.value,
      inboxId: props.inboxId,
    });

    // name é imutável em edição (input disabled)
    if (!isEditMode.value && draft.name) name.value = draft.name;
    if (draft.category) category.value = draft.category;
    if (draft.language && !isEditMode.value) language.value = draft.language;
    // A IA só gera headers NONE/TEXT (mídia exige upload manual depois)
    headerType.value = draft.header_type || 'NONE';
    headerText.value = draft.header_text || '';
    bodyText.value = draft.body || '';
    footerText.value = draft.footer || '';
    buttons.value = Array.isArray(draft.buttons)
      ? draft.buttons.map(b => ({
          type: b.type,
          text: b.text || '',
          url: b.url || '',
          phone: b.phone || '',
        }))
      : [];

    // os watchers redimensionam bodyExamples quando bodyText muda; aguarda o
    // próximo tick para então preencher os exemplos retornados pela IA
    await nextTick();
    if (Array.isArray(draft.body_examples)) {
      draft.body_examples.forEach((ex, i) => {
        bodyExamples.value[i] = ex;
      });
    }

    aiPromptOpen.value = false;
  } catch (error) {
    const msg =
      error?.response?.data?.error ||
      error?.message ||
      t('WHATSAPP_TEMPLATES.FORM.AI.ERROR');
    useAlert(msg);
  } finally {
    aiGenerating.value = false;
  }
};

// ── Validation ───────────────────────────────────────────────────────────────

const nameError = computed(() => {
  if (!name.value) return '';
  if (!name.value.match(/^[a-z0-9_]+$/)) {
    return t('WHATSAPP_TEMPLATES.FORM.NAME_FORMAT_ERROR');
  }
  return '';
});

const allExamplesFilled = computed(() => {
  const bodyOk = bodyExamples.value.every(e => e && e.trim().length > 0);
  const headerOk = headerExamples.value.every(e => e && e.trim().length > 0);
  return bodyOk && headerOk;
});

const isValid = computed(() => {
  return (
    name.value &&
    !nameError.value &&
    bodyText.value &&
    language.value &&
    category.value &&
    !namedVarError.value &&
    allExamplesFilled.value &&
    // NOOVI: header de mídia exige upload concluído (handle) (whatsapp-template-media)
    (!isMediaHeader.value || !!headerHandle.value)
  );
});

const bodyVariableCount = computed(() => extractBodyVars.value.length);

// ── Button management ────────────────────────────────────────────────────────

const addButton = () => {
  if (buttons.value.length >= 10) return;
  // Task #8: default type for AUTHENTICATION is COPY_CODE
  const defaultType =
    category.value === 'AUTHENTICATION' ? 'COPY_CODE' : 'QUICK_REPLY';
  buttons.value.push({ type: defaultType, text: '' });
};

const removeButton = index => {
  buttons.value.splice(index, 1);
};

// ── Task #12: insert var at cursor in body textarea ──────────────────────────

const formatVarTag = varName => `{{${varName}}}`;

const insertVarAtCursor = varName => {
  const el = bodyTextareaRef.value;
  const insertion = `{{${varName}}}`;
  if (!el) {
    bodyText.value += insertion;
    return;
  }
  const start = el.selectionStart;
  const end = el.selectionEnd;
  bodyText.value =
    bodyText.value.slice(0, start) + insertion + bodyText.value.slice(end);
  // Restore focus and cursor position after Vue updates DOM
  el.focus();
  const newPos = start + insertion.length;
  setTimeout(() => {
    el.setSelectionRange(newPos, newPos);
  }, 0);
};

// ── buildComponents (Task #7 + #8 + #10) ─────────────────────────────────────

const buildComponents = () => {
  const components = [];
  const isNamed = parameterFormat.value === 'NAMED';

  if (headerType.value !== 'NONE') {
    const header = { type: 'HEADER', format: headerType.value };
    // NOOVI: header de mídia carrega o handle do arquivo de exemplo
    // (whatsapp-template-media)
    if (MEDIA_HEADER_TYPES.includes(headerType.value) && headerHandle.value) {
      header.example = { header_handle: [headerHandle.value] };
    }
    if (headerType.value === 'TEXT' && headerText.value) {
      header.text = headerText.value;
      if (isNamed) {
        const vars = extractHeaderVars.value;
        if (vars.length > 0) {
          header.example = {
            header_text_named_params: vars.map((v, i) => ({
              param_name: v,
              example: headerExamples.value[i] || v,
            })),
          };
        }
      } else {
        const vars = headerText.value.match(positionalVarRegex);
        if (vars) {
          header.example = {
            header_text: vars.map(
              (_, i) => headerExamples.value[i] || 'Example'
            ),
          };
        }
      }
    }
    components.push(header);
  }

  const body = { type: 'BODY', text: bodyText.value };
  if (isNamed) {
    const vars = extractBodyVars.value;
    if (vars.length > 0) {
      body.example = {
        body_text_named_params: vars.map((v, i) => ({
          param_name: v,
          example: bodyExamples.value[i] || v,
        })),
      };
    }
  } else {
    const bodyVars = bodyText.value.match(positionalVarRegex);
    if (bodyVars) {
      body.example = {
        body_text: [bodyVars.map((_, i) => bodyExamples.value[i] || 'Example')],
      };
    }
  }
  components.push(body);

  if (footerText.value) {
    components.push({ type: 'FOOTER', text: footerText.value });
  }

  if (buttons.value.length > 0) {
    const validButtons = buttons.value
      .filter(btn => btn.text)
      .map(btn => {
        // Task #8: COPY_CODE only needs text
        if (btn.type === 'COPY_CODE') {
          return { type: 'COPY_CODE', text: btn.text };
        }
        const result = { type: btn.type, text: btn.text };
        if (btn.type === 'URL' && btn.url) result.url = btn.url;
        if (btn.type === 'PHONE_NUMBER' && btn.phone)
          result.phone_number = btn.phone;
        return result;
      });
    if (validButtons.length) {
      components.push({ type: 'BUTTONS', buttons: validButtons });
    }
  }

  return components;
};

// ── Submit ────────────────────────────────────────────────────────────────────

const handleSubmit = async () => {
  if (!isValid.value) return;

  loading.value = true;
  try {
    if (isEditMode.value) {
      await store.dispatch('whatsappTemplates/updateTemplate', {
        templateId: props.template.id,
        templateData: {
          category: category.value,
          components: buildComponents(),
        },
      });
    } else {
      const payload = {
        name: name.value,
        language: language.value,
        category: category.value,
        allow_category_change: true,
        components: buildComponents(),
      };
      // Task #10: include parameter_format only for NAMED
      if (parameterFormat.value === 'NAMED') {
        payload.parameter_format = 'NAMED';
      }
      await store.dispatch('whatsappTemplates/createTemplate', payload);
    }
    emit('saved');
  } catch (error) {
    const msg =
      error?.response?.data?.error ||
      error?.message ||
      t('WHATSAPP_TEMPLATES.FORM.CREATE_ERROR');
    useAlert(msg);
  } finally {
    loading.value = false;
  }
};
</script>

<template>
  <!-- Modal overlay — click on backdrop closes the form -->
  <div
    class="fixed inset-0 z-[9990] flex items-start sm:items-center justify-center overflow-y-auto bg-n-alpha-black2 backdrop-blur-[4px] p-4"
    @click.self="emit('close')"
  >
    <div
      class="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-[1.5rem] bg-n-background shadow-2xl ring-1 ring-white/10"
    >
      <!-- Premium Header -->
      <div
        class="sticky top-0 z-10 flex items-center justify-between px-8 py-5 border-b border-n-weak/80 bg-n-background/90 backdrop-blur-md rounded-t-[1.5rem]"
      >
        <div class="flex items-center gap-4">
          <div
            class="flex size-11 items-center justify-center rounded-2xl bg-[#25D366]/10 text-[#25D366] ring-1 ring-[#25D366]/20 shadow-inner"
          >
            <span class="i-lucide-message-square size-5" aria-hidden="true" />
          </div>
          <div>
            <h2 class="text-xl font-bold text-n-slate-12 tracking-tight">
              {{
                isEditMode
                  ? t('WHATSAPP_TEMPLATES.FORM.EDIT_TITLE')
                  : t('WHATSAPP_TEMPLATES.FORM.TITLE')
              }}
            </h2>
            <p
              class="text-xs font-semibold text-n-slate-10 mt-0.5 uppercase tracking-wide"
            >
              WhatsApp API
            </p>
          </div>
        </div>
        <button
          class="flex size-9 items-center justify-center rounded-xl bg-n-alpha-1 text-n-slate-10 hover:bg-ruby-3 hover:text-ruby-11 transition-colors focus:outline-none focus:ring-2 focus:ring-ruby-9/20"
          @click="emit('close')"
        >
          <span class="i-lucide-x size-4.5" />
        </button>
      </div>

      <div class="flex flex-col lg:flex-row h-full">
        <!-- Left Side: Form Fields -->
        <div class="flex-1 p-8 lg:border-r border-n-weak/80 bg-n-background">
          <form class="flex flex-col gap-6" @submit.prevent="handleSubmit">
            <!-- NOOVI: AI generation panel (whatsapp-template-ai) -->
            <div class="rounded-2xl border border-n-brand/30 bg-n-brand/5 p-4">
              <button
                type="button"
                class="w-full flex items-center justify-between text-left"
                @click="aiPromptOpen = !aiPromptOpen"
              >
                <span
                  class="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-n-brand"
                >
                  <span class="i-lucide-sparkles size-4" />
                  {{ t('WHATSAPP_TEMPLATES.FORM.AI.BUTTON') }}
                </span>
                <span
                  class="text-n-slate-10 transition-transform"
                  :class="aiPromptOpen ? 'rotate-180' : ''"
                >
                  <span class="i-lucide-chevron-down size-4" />
                </span>
              </button>

              <div v-if="aiPromptOpen" class="mt-3 space-y-2">
                <textarea
                  v-model="aiPrompt"
                  :placeholder="
                    t('WHATSAPP_TEMPLATES.FORM.AI.PROMPT_PLACEHOLDER')
                  "
                  class="w-full min-h-[72px] resize-y rounded-xl border border-n-strong bg-n-background px-3 py-2 text-sm font-medium text-n-slate-12 placeholder-n-slate-9 focus:border-n-brand focus:outline-none focus:ring-2 focus:ring-n-brand/20"
                  maxlength="600"
                  @keydown.stop
                />
                <button
                  type="button"
                  class="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold text-white transition-all focus:outline-none focus:ring-2 focus:ring-n-brand/30"
                  :class="
                    aiGenerating || !aiPrompt.trim()
                      ? 'bg-n-alpha-2 text-n-slate-9 cursor-not-allowed'
                      : 'bg-n-brand hover:bg-n-brand/90'
                  "
                  :disabled="aiGenerating || !aiPrompt.trim()"
                  @click="handleGenerateAi"
                >
                  <span
                    v-if="aiGenerating"
                    class="i-lucide-loader-2 size-3.5 animate-spin"
                  />
                  <span v-else class="i-lucide-wand-2 size-3.5" />
                  {{
                    aiGenerating
                      ? t('WHATSAPP_TEMPLATES.FORM.AI.GENERATING')
                      : t('WHATSAPP_TEMPLATES.FORM.AI.GENERATE')
                  }}
                </button>
                <p class="text-[11px] text-n-slate-10">
                  {{ t('WHATSAPP_TEMPLATES.FORM.AI.HINT') }}
                </p>
              </div>
            </div>

            <!-- Name, Lang, Category Group -->
            <div
              class="space-y-4 bg-n-alpha-1/30 p-5 rounded-2xl border border-n-strong/30"
            >
              <h3
                class="text-xs font-bold uppercase tracking-wider text-n-slate-11 mb-2 flex items-center gap-2"
              >
                <span class="i-lucide-settings-2 size-4" />
                General Settings
              </h3>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="md:col-span-2">
                  <label
                    class="block text-xs font-bold uppercase tracking-wide text-n-slate-11 mb-1.5"
                  >
                    {{ t('WHATSAPP_TEMPLATES.FORM.NAME') }}
                  </label>
                  <input
                    v-model="name"
                    type="text"
                    placeholder="e.g. order_confirmation"
                    :disabled="isEditMode"
                    class="w-full border rounded-xl px-4 py-2.5 text-sm font-medium transition-all shadow-sm focus:outline-none focus:ring-2 placeholder-n-slate-9"
                    :class="[
                      nameError
                        ? 'border-ruby-9 focus:border-ruby-9 focus:ring-ruby-9/20 bg-n-background text-n-slate-12'
                        : 'border-n-strong hover:bg-n-alpha-1/50 focus:border-n-brand focus:ring-n-brand/20 bg-n-background text-n-slate-12',
                      isEditMode
                        ? 'opacity-60 cursor-not-allowed bg-n-alpha-1'
                        : '',
                    ]"
                  />
                  <p
                    v-if="nameError"
                    class="mt-1.5 text-[11px] font-bold text-ruby-11 flex items-center gap-1"
                  >
                    <span class="i-lucide-alert-circle size-3" />
                    {{ nameError }}
                  </p>
                </div>

                <div>
                  <label
                    class="block text-xs font-bold uppercase tracking-wide text-n-slate-11 mb-1.5"
                  >
                    {{ t('WHATSAPP_TEMPLATES.FORM.LANGUAGE') }}
                  </label>
                  <div class="relative">
                    <select
                      v-model="language"
                      :disabled="isEditMode"
                      class="!mb-0 appearance-none bg-none w-full border rounded-xl pl-4 pr-10 py-2.5 text-sm font-medium transition-all shadow-sm focus:outline-none focus:ring-2 cursor-pointer"
                      :class="[
                        isEditMode
                          ? 'border-n-strong opacity-60 cursor-not-allowed bg-n-alpha-1 text-n-slate-12'
                          : 'border-n-strong hover:bg-n-alpha-1/50 focus:border-n-brand focus:ring-n-brand/20 bg-n-background text-n-slate-12',
                      ]"
                    >
                      <option
                        v-for="lang in LANGUAGES"
                        :key="lang.value"
                        :value="lang.value"
                      >
                        {{ lang.label }}
                      </option>
                    </select>
                    <span
                      class="i-lucide-chevron-down absolute right-4 top-1/2 -translate-y-1/2 size-4 text-n-slate-10 pointer-events-none"
                    />
                  </div>
                </div>

                <div>
                  <label
                    class="block text-xs font-bold uppercase tracking-wide text-n-slate-11 mb-1.5"
                  >
                    {{ t('WHATSAPP_TEMPLATES.FORM.CATEGORY') }}
                  </label>
                  <div class="relative">
                    <select
                      v-model="category"
                      class="!mb-0 appearance-none bg-none w-full border border-n-strong rounded-xl pl-4 pr-10 py-2.5 text-sm font-medium bg-n-background text-n-slate-12 transition-all shadow-sm hover:bg-n-alpha-1/50 focus:outline-none focus:ring-2 focus:ring-n-brand/20 focus:border-n-brand cursor-pointer"
                    >
                      <option
                        v-for="cat in CATEGORIES"
                        :key="cat.value"
                        :value="cat.value"
                      >
                        {{ cat.label }}
                      </option>
                    </select>
                    <span
                      class="i-lucide-chevron-down absolute right-4 top-1/2 -translate-y-1/2 size-4 text-n-slate-10 pointer-events-none"
                    />
                  </div>
                </div>
              </div>

              <!-- Task #10: Parameter Format Toggle -->
              <div>
                <label
                  class="block text-xs font-bold uppercase tracking-wide text-n-slate-11 mb-2"
                >
                  {{ t('WHATSAPP_TEMPLATES.FORM.PARAMETER_FORMAT') }}
                </label>
                <div class="flex gap-2">
                  <button
                    type="button"
                    class="flex-1 px-3 py-2 rounded-xl text-xs font-semibold border transition-all focus:outline-none focus:ring-2 focus:ring-n-brand/20"
                    :class="
                      parameterFormat === 'POSITIONAL'
                        ? 'bg-n-brand text-white border-n-brand shadow-sm'
                        : 'bg-n-background text-n-slate-11 border-n-strong hover:bg-n-alpha-1/50'
                    "
                    @click="parameterFormat = 'POSITIONAL'"
                  >
                    {{ t('WHATSAPP_TEMPLATES.FORM.FORMAT_POSITIONAL') }}
                  </button>
                  <button
                    type="button"
                    class="flex-1 px-3 py-2 rounded-xl text-xs font-semibold border transition-all focus:outline-none focus:ring-2 focus:ring-n-brand/20"
                    :class="
                      parameterFormat === 'NAMED'
                        ? 'bg-n-brand text-white border-n-brand shadow-sm'
                        : 'bg-n-background text-n-slate-11 border-n-strong hover:bg-n-alpha-1/50'
                    "
                    @click="parameterFormat = 'NAMED'"
                  >
                    {{ t('WHATSAPP_TEMPLATES.FORM.FORMAT_NAMED') }}
                  </button>
                </div>
              </div>
            </div>

            <!-- Template Content Group -->
            <div class="space-y-5">
              <!-- Header -->
              <div>
                <label
                  class="block text-xs font-bold uppercase tracking-wide text-n-slate-11 mb-1.5"
                >
                  {{ t('WHATSAPP_TEMPLATES.FORM.HEADER') }}
                </label>
                <div class="flex flex-col sm:flex-row gap-3">
                  <div class="relative w-full sm:w-1/3">
                    <select
                      v-model="headerType"
                      class="!mb-0 appearance-none bg-none w-full border border-n-strong rounded-xl pl-4 pr-10 py-2.5 text-sm font-medium bg-n-background text-n-slate-12 transition-all shadow-sm hover:bg-n-alpha-1/50 focus:outline-none focus:ring-2 focus:ring-n-brand/20 focus:border-n-brand cursor-pointer"
                    >
                      <option
                        v-for="ht in HEADER_TYPES"
                        :key="ht.value"
                        :value="ht.value"
                      >
                        {{ ht.label }}
                      </option>
                    </select>
                    <span
                      class="i-lucide-chevron-down absolute right-4 top-1/2 -translate-y-1/2 size-4 text-n-slate-10 pointer-events-none"
                    />
                  </div>

                  <div v-if="headerType === 'TEXT'" class="flex-1">
                    <input
                      v-model="headerText"
                      type="text"
                      :placeholder="
                        t('WHATSAPP_TEMPLATES.FORM.HEADER_TEXT_PLACEHOLDER')
                      "
                      class="w-full border border-n-strong rounded-xl px-4 py-2.5 text-sm font-medium bg-n-background text-n-slate-12 transition-all shadow-sm hover:bg-n-alpha-1/50 focus:outline-none focus:ring-2 focus:ring-n-brand/20 focus:border-n-brand placeholder-n-slate-9"
                      maxlength="60"
                    />
                  </div>
                </div>

                <!-- NOOVI: media header upload (whatsapp-template-media) -->
                <div v-if="isMediaHeader" class="mt-3">
                  <input
                    ref="mediaInputRef"
                    type="file"
                    class="hidden"
                    :accept="mediaAccept"
                    @change="handleMediaSelected"
                  />
                  <div
                    class="flex items-center gap-3 p-3 rounded-xl bg-n-alpha-1/50 border border-dashed border-n-strong/60"
                  >
                    <button
                      type="button"
                      class="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-bold text-n-brand bg-n-brand/10 hover:bg-n-brand/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      :disabled="uploadingMedia"
                      @click="triggerMediaPicker"
                    >
                      <span
                        v-if="uploadingMedia"
                        class="i-lucide-loader-2 size-3.5 animate-spin"
                      />
                      <span v-else class="i-lucide-upload size-3.5" />
                      {{
                        headerHandle
                          ? t('WHATSAPP_TEMPLATES.FORM.MEDIA.REPLACE')
                          : t('WHATSAPP_TEMPLATES.FORM.MEDIA.UPLOAD')
                      }}
                    </button>
                    <div class="min-w-0 flex-1">
                      <p
                        v-if="headerMediaName"
                        class="text-xs font-medium text-n-slate-12 truncate flex items-center gap-1.5"
                      >
                        <span
                          class="i-lucide-check-circle size-3.5 text-[#25D366]"
                        />
                        {{ headerMediaName }}
                      </p>
                      <p v-else class="text-[11px] text-n-slate-10">
                        {{ t('WHATSAPP_TEMPLATES.FORM.MEDIA.HINT') }}
                      </p>
                    </div>
                  </div>
                  <p
                    v-if="mediaError"
                    class="mt-1.5 text-[11px] font-bold text-ruby-11 flex items-center gap-1"
                  >
                    <span class="i-lucide-alert-circle size-3" />
                    {{ mediaError }}
                  </p>
                </div>

                <!-- Task #7: Header example inputs -->
                <div
                  v-if="headerType === 'TEXT' && extractHeaderVars.length > 0"
                  class="mt-3 p-3 rounded-xl bg-n-alpha-1/50 border border-n-strong/40 space-y-2"
                >
                  <p
                    class="text-[11px] font-bold uppercase tracking-wide text-n-slate-10"
                  >
                    {{ t('WHATSAPP_TEMPLATES.FORM.EXAMPLES_LABEL') }}
                  </p>
                  <div
                    v-for="(varItem, idx) in extractHeaderVars"
                    :key="'hdr-ex-' + idx"
                  >
                    <label
                      class="block text-[11px] font-medium text-n-slate-10 mb-1"
                    >
                      {{
                        t('WHATSAPP_TEMPLATES.FORM.EXAMPLE_FOR_VAR', {
                          var: varItem,
                        })
                      }}
                    </label>
                    <input
                      v-model="headerExamples[idx]"
                      type="text"
                      :placeholder="
                        t('WHATSAPP_TEMPLATES.FORM.EXAMPLE_PLACEHOLDER')
                      "
                      class="w-full border border-n-strong rounded-lg px-3 py-1.5 text-xs font-medium bg-n-background text-n-slate-12 focus:outline-none focus:border-n-brand focus:ring-2 focus:ring-n-brand/20 placeholder-n-slate-9"
                    />
                  </div>
                </div>
              </div>

              <!-- Body -->
              <div>
                <div class="flex items-center justify-between mb-1.5">
                  <label
                    class="block text-xs font-bold uppercase tracking-wide text-n-slate-11"
                  >
                    {{ t('WHATSAPP_TEMPLATES.FORM.BODY') }}
                    <span class="text-ruby-9">*</span>
                  </label>
                  <span
                    class="text-[10px] font-bold text-n-slate-10 bg-n-alpha-1 px-2 py-0.5 rounded-md"
                  >
                    {{ bodyText.length }}/1024
                  </span>
                </div>
                <textarea
                  ref="bodyTextareaRef"
                  v-model="bodyText"
                  :placeholder="
                    parameterFormat === 'NAMED'
                      ? t('WHATSAPP_TEMPLATES.FORM.BODY_PLACEHOLDER_NAMED')
                      : t('WHATSAPP_TEMPLATES.FORM.BODY_PLACEHOLDER')
                  "
                  class="w-full min-h-[140px] resize-y rounded-xl border border-n-strong bg-n-background px-4 py-3 text-sm font-medium text-n-slate-12 placeholder-n-slate-9 transition-all shadow-sm hover:bg-n-alpha-1/50 focus:border-n-brand focus:outline-none focus:ring-2 focus:ring-n-brand/20 custom-scrollbar"
                  maxlength="1024"
                />

                <!-- Task #10: Named var format error -->
                <p
                  v-if="namedVarError"
                  class="mt-1.5 text-[11px] font-bold text-ruby-11 flex items-center gap-1"
                >
                  <span class="i-lucide-alert-circle size-3" />
                  {{ namedVarError }}
                </p>

                <div class="flex items-center justify-between mt-1.5">
                  <p
                    class="text-[11px] font-medium text-n-slate-10 flex items-center gap-1.5"
                  >
                    <span class="i-lucide-info size-3.5" />
                    {{
                      parameterFormat === 'NAMED'
                        ? t('WHATSAPP_TEMPLATES.FORM.BODY_VARIABLES_HINT_NAMED')
                        : t('WHATSAPP_TEMPLATES.FORM.BODY_VARIABLES_HINT')
                    }}
                  </p>
                  <span
                    v-if="bodyVariableCount"
                    class="text-[10px] font-bold text-n-brand bg-n-brand/10 border border-n-brand/20 px-2 py-0.5 rounded-md"
                  >
                    {{ bodyVariableCount }}
                    {{ t('WHATSAPP_TEMPLATES.FORM.VARIABLES') }}
                  </span>
                </div>

                <!-- Task #7: Body example inputs -->
                <div
                  v-if="extractBodyVars.length > 0"
                  class="mt-3 p-3 rounded-xl bg-n-alpha-1/50 border border-n-strong/40 space-y-2"
                >
                  <p
                    class="text-[11px] font-bold uppercase tracking-wide text-n-slate-10"
                  >
                    {{ t('WHATSAPP_TEMPLATES.FORM.EXAMPLES_LABEL') }}
                  </p>
                  <div
                    v-for="(varItem, idx) in extractBodyVars"
                    :key="'body-ex-' + idx"
                  >
                    <label
                      class="block text-[11px] font-medium text-n-slate-10 mb-1"
                    >
                      {{
                        t('WHATSAPP_TEMPLATES.FORM.EXAMPLE_FOR_VAR', {
                          var: varItem,
                        })
                      }}
                    </label>
                    <input
                      v-model="bodyExamples[idx]"
                      type="text"
                      :placeholder="
                        t('WHATSAPP_TEMPLATES.FORM.EXAMPLE_PLACEHOLDER')
                      "
                      class="w-full border border-n-strong rounded-lg px-3 py-1.5 text-xs font-medium bg-n-background text-n-slate-12 focus:outline-none focus:border-n-brand focus:ring-2 focus:ring-n-brand/20 placeholder-n-slate-9"
                    />
                  </div>
                </div>

                <!-- Task #12: Named vars hint accordion -->
                <div
                  v-if="parameterFormat === 'NAMED'"
                  class="mt-3 border border-n-strong/40 rounded-xl overflow-hidden"
                >
                  <button
                    type="button"
                    class="w-full flex items-center justify-between px-4 py-2.5 bg-n-alpha-1/50 hover:bg-n-alpha-1 transition-colors text-left"
                    @click="namedVarsHintOpen = !namedVarsHintOpen"
                  >
                    <span
                      class="flex items-center gap-2 text-[11px] font-bold text-n-slate-11 uppercase tracking-wide"
                    >
                      <span class="i-lucide-sparkles size-3.5 text-n-brand" />
                      {{ t('WHATSAPP_TEMPLATES.FORM.NAMED_VARS_HELP.TITLE') }}
                    </span>
                    <span
                      class="text-n-slate-10 transition-transform"
                      :class="namedVarsHintOpen ? 'rotate-180' : ''"
                    >
                      <span class="i-lucide-chevron-down size-4" />
                    </span>
                  </button>

                  <div v-if="namedVarsHintOpen" class="px-4 py-3 space-y-3">
                    <p class="text-[11px] text-n-slate-10">
                      {{
                        t('WHATSAPP_TEMPLATES.FORM.NAMED_VARS_HELP.SUBTITLE')
                      }}
                    </p>

                    <div
                      v-for="group in NAMED_VARS_GROUPS"
                      :key="group.labelKey"
                      class="space-y-1.5"
                    >
                      <p
                        class="text-[10px] font-bold uppercase tracking-wider text-n-slate-10"
                      >
                        {{ t(group.labelKey) }}
                      </p>
                      <div class="flex flex-wrap gap-1.5">
                        <button
                          v-for="varName in group.vars"
                          :key="varName"
                          type="button"
                          class="inline-flex items-center px-2 py-1 rounded-md bg-n-brand/10 text-n-brand border border-n-brand/20 text-[11px] font-mono font-medium hover:bg-n-brand/20 transition-colors cursor-pointer"
                          :title="
                            t(
                              'WHATSAPP_TEMPLATES.FORM.NAMED_VARS_HELP.INSERT_HINT'
                            )
                          "
                          @click="insertVarAtCursor(varName)"
                        >
                          {{ formatVarTag(varName) }}
                        </button>
                      </div>
                    </div>

                    <!-- Custom attributes note -->
                    <div class="pt-1 border-t border-n-strong/30">
                      <p
                        class="text-[10px] font-bold uppercase tracking-wider text-n-slate-10 mb-1"
                      >
                        {{
                          t(
                            'WHATSAPP_TEMPLATES.FORM.NAMED_VARS_HELP.GROUP_CUSTOM'
                          )
                        }}
                      </p>
                      <p class="text-[11px] text-n-slate-10">
                        {{
                          t(
                            'WHATSAPP_TEMPLATES.FORM.NAMED_VARS_HELP.CUSTOM_ATTRS_NOTE'
                          )
                        }}
                      </p>
                      <p class="text-[11px] text-n-slate-9 mt-2">
                        ⚠
                        {{
                          t(
                            'WHATSAPP_TEMPLATES.FORM.NAMED_VARS_HELP.UNKNOWN_VARS_WARNING'
                          )
                        }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Footer -->
              <div>
                <label
                  class="block text-xs font-bold uppercase tracking-wide text-n-slate-11 mb-1.5"
                >
                  {{ t('WHATSAPP_TEMPLATES.FORM.FOOTER') }}
                  <span class="text-[10px] font-medium opacity-70 lowercase"
                    >(optional)</span
                  >
                </label>
                <input
                  v-model="footerText"
                  type="text"
                  :placeholder="t('WHATSAPP_TEMPLATES.FORM.FOOTER_PLACEHOLDER')"
                  class="w-full border border-n-strong rounded-xl px-4 py-2.5 text-sm font-medium bg-n-background text-n-slate-12 transition-all shadow-sm hover:bg-n-alpha-1/50 focus:outline-none focus:ring-2 focus:ring-n-brand/20 focus:border-n-brand placeholder-n-slate-9"
                  maxlength="60"
                />
              </div>

              <!-- Buttons Configuration -->
              <div class="pt-2">
                <div class="flex items-center justify-between mb-3">
                  <label
                    class="block text-xs font-bold uppercase tracking-wide text-n-slate-11"
                  >
                    Interactive Buttons
                  </label>
                  <button
                    v-if="buttons.length < 10"
                    type="button"
                    class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-n-brand bg-n-brand/10 hover:bg-n-brand/20 transition-colors"
                    @click="addButton"
                  >
                    <span class="i-lucide-plus size-3.5" />
                    Add Button
                  </button>
                </div>

                <div
                  v-if="buttons.length === 0"
                  class="flex flex-col items-center justify-center p-6 border border-dashed border-n-strong rounded-xl bg-n-alpha-1/30"
                >
                  <span
                    class="i-lucide-mouse-pointer-click size-6 text-n-slate-9 mb-2"
                  />
                  <p class="text-sm font-medium text-n-slate-10">
                    No buttons added
                  </p>
                </div>

                <div class="space-y-3">
                  <div
                    v-for="(btn, idx) in buttons"
                    :key="idx"
                    class="group flex flex-col sm:flex-row items-stretch gap-2 p-3 border border-n-weak rounded-xl bg-n-background shadow-sm relative transition-all hover:border-n-strong"
                  >
                    <div class="relative w-full sm:w-1/3">
                      <select
                        v-model="btn.type"
                        class="!mb-0 appearance-none bg-none w-full border border-n-strong rounded-lg pl-3 pr-8 py-2 text-xs font-bold bg-n-alpha-1 text-n-slate-12 transition-all focus:outline-none focus:ring-2 focus:ring-n-brand/20 cursor-pointer h-full"
                      >
                        <option value="QUICK_REPLY">Quick Reply</option>
                        <option value="URL">URL Link</option>
                        <option value="PHONE_NUMBER">Phone Call</option>
                        <!-- Task #8: COPY_CODE only for AUTHENTICATION -->
                        <option
                          v-if="category === 'AUTHENTICATION'"
                          value="COPY_CODE"
                        >
                          Copy Code
                        </option>
                      </select>
                      <span
                        class="i-lucide-chevron-down absolute right-2.5 top-1/2 -translate-y-1/2 size-3.5 text-n-slate-10 pointer-events-none"
                      />
                    </div>

                    <input
                      v-model="btn.text"
                      type="text"
                      placeholder="Button Label"
                      class="w-full sm:w-1/3 border border-n-strong rounded-lg px-3 py-2 text-xs font-medium bg-n-background text-n-slate-12 focus:outline-none focus:border-n-brand focus:ring-2 focus:ring-n-brand/20 placeholder-n-slate-9"
                      maxlength="25"
                      @keydown.enter.prevent
                    />

                    <!-- URL and Phone fields hidden for COPY_CODE -->
                    <input
                      v-if="btn.type === 'URL'"
                      v-model="btn.url"
                      type="text"
                      placeholder="https://example.com"
                      class="w-full sm:flex-1 border border-n-strong rounded-lg px-3 py-2 text-xs font-medium bg-n-background text-n-slate-12 focus:outline-none focus:border-n-brand focus:ring-2 focus:ring-n-brand/20 placeholder-n-slate-9"
                      @keydown.enter.prevent
                    />
                    <input
                      v-if="btn.type === 'PHONE_NUMBER'"
                      v-model="btn.phone"
                      type="text"
                      placeholder="+1234567890"
                      class="w-full sm:flex-1 border border-n-strong rounded-lg px-3 py-2 text-xs font-medium bg-n-background text-n-slate-12 focus:outline-none focus:border-n-brand focus:ring-2 focus:ring-n-brand/20 placeholder-n-slate-9"
                      @keydown.enter.prevent
                    />

                    <button
                      type="button"
                      class="absolute -top-2 -right-2 size-6 flex items-center justify-center rounded-full bg-n-background border border-n-strong text-n-slate-10 hover:text-ruby-11 hover:border-ruby-5 hover:bg-ruby-2 shadow-sm transition-all opacity-0 group-hover:opacity-100"
                      @click="removeButton(idx)"
                    >
                      <span class="i-lucide-x size-3" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>

        <!-- Right Side: Preview -->
        <div
          class="w-full lg:w-80 flex-shrink-0 bg-n-slate-2 dark:bg-n-background p-6 flex flex-col rounded-b-[1.5rem] lg:rounded-bl-none lg:rounded-br-[1.5rem] relative overflow-hidden"
        >
          <!-- Background pattern (self-contained inline SVG — no external CDN) -->
          <div
            class="absolute inset-0 opacity-[0.05] dark:opacity-[0.07] pointer-events-none bg-repeat"
            :style="{ backgroundImage: PREVIEW_PATTERN }"
          />

          <h3
            class="text-xs font-bold uppercase tracking-wider text-n-slate-11 mb-4 flex items-center gap-2 relative z-10"
          >
            <span class="i-lucide-eye size-4" />
            Live Preview
          </h3>

          <div class="flex-1 flex flex-col items-center relative z-10">
            <!-- Message Bubble Premium -->
            <div
              v-if="bodyText"
              class="w-full bg-[#E7FFDB] dark:bg-[#005C4B] rounded-2xl rounded-tr-sm p-4 shadow-sm relative text-left"
            >
              <!-- Header Preview -->
              <p
                v-if="headerType === 'TEXT' && headerText"
                class="text-sm font-bold text-[#111B21] dark:text-white mb-2 leading-snug"
              >
                {{ headerText }}
              </p>
              <!-- NOOVI: preview da imagem enviada quando disponível (whatsapp-template-media) -->
              <img
                v-if="headerType === 'IMAGE' && headerMediaPreviewUrl"
                :src="headerMediaPreviewUrl"
                alt=""
                class="w-full h-32 object-cover rounded-lg mb-2 border border-black/5"
              />
              <div
                v-else-if="headerType !== 'NONE' && headerType !== 'TEXT'"
                class="h-24 bg-black/5 dark:bg-black/20 rounded-lg flex items-center justify-center mb-2 border border-black/5"
              >
                <span
                  class="text-xs font-bold text-black/40 dark:text-white/40 uppercase flex items-center gap-1"
                >
                  <span
                    v-if="headerType === 'IMAGE'"
                    class="i-lucide-image size-4"
                  />
                  <span
                    v-if="headerType === 'VIDEO'"
                    class="i-lucide-video size-4"
                  />
                  <span
                    v-if="headerType === 'DOCUMENT'"
                    class="i-lucide-file size-4"
                  />
                  {{ headerType }}
                </span>
              </div>

              <!-- Body Preview -->
              <p
                class="text-[14px] text-[#111B21] dark:text-[#E9EDEF] whitespace-pre-wrap leading-[1.4] font-normal break-words"
              >
                {{ bodyText }}
              </p>

              <!-- Footer Preview -->
              <p
                v-if="footerText"
                class="text-[11px] text-[#667781] dark:text-[#8696A0] mt-2 pt-2 border-t border-black/5 dark:border-white/10"
              >
                {{ footerText }}
              </p>

              <div
                class="text-[10px] text-[#667781] dark:text-[#8696A0] flex justify-end items-center gap-1 mt-1"
              >
                12:00
                <span class="i-lucide-check-check size-3.5 text-[#53bdeb]" />
              </div>
            </div>

            <div
              v-else
              class="w-full flex flex-col items-center justify-center p-8 text-center text-n-slate-10 bg-white/50 dark:bg-n-alpha-1/50 rounded-2xl border border-n-weak/50 backdrop-blur-sm"
            >
              <span
                class="i-lucide-message-square-dashed size-8 mb-3 opacity-50"
              />
              <p class="text-sm font-medium">Type a message to see preview</p>
            </div>

            <!-- Buttons Preview -->
            <div
              v-if="buttons.length && bodyText"
              class="w-full mt-1.5 space-y-1.5"
            >
              <div
                v-for="(btn, idx) in buttons"
                :key="'prev-' + idx"
                class="w-full bg-white dark:bg-[#202C33] rounded-xl py-2.5 px-3 shadow-sm border border-black/5 dark:border-white/5 flex items-center justify-center gap-2 cursor-default"
              >
                <span
                  v-if="btn.type === 'URL'"
                  class="i-lucide-external-link size-3.5 text-[#00A884]"
                />
                <span
                  v-if="btn.type === 'PHONE_NUMBER'"
                  class="i-lucide-phone size-3.5 text-[#00A884]"
                />
                <span
                  v-if="btn.type === 'QUICK_REPLY'"
                  class="i-lucide-corner-down-right size-3.5 text-[#00A884]"
                />
                <span
                  v-if="btn.type === 'COPY_CODE'"
                  class="i-lucide-copy size-3.5 text-[#00A884]"
                />
                <span class="text-[#00A884] font-semibold text-sm">{{
                  btn.text || 'Button'
                }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Premium Footer Actions -->
      <div
        class="px-8 py-5 border-t border-n-weak/80 bg-n-alpha-1/30 rounded-b-[1.5rem] flex items-center justify-end gap-3"
      >
        <button
          type="button"
          class="px-5 py-2.5 text-sm font-semibold text-n-slate-11 hover:text-n-slate-12 rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-n-slate-6"
          @click="emit('close')"
        >
          {{ t('WHATSAPP_TEMPLATES.FORM.CANCEL') }}
        </button>
        <button
          type="button"
          class="flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white rounded-xl shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-offset-2"
          :class="
            !isValid || loading
              ? 'bg-n-alpha-2 text-n-slate-9 cursor-not-allowed border border-n-weak'
              : 'bg-n-brand hover:bg-n-brand/90 hover:shadow-lg hover:-translate-y-px focus:ring-n-brand'
          "
          :disabled="!isValid || loading"
          @click="handleSubmit"
        >
          <span
            v-if="loading"
            class="i-lucide-loader-2 size-4.5 animate-spin"
            aria-hidden="true"
          />
          <span v-else class="i-lucide-check-circle size-4.5" />
          {{
            isEditMode
              ? t('WHATSAPP_TEMPLATES.FORM.UPDATE')
              : t('WHATSAPP_TEMPLATES.FORM.SUBMIT')
          }}
        </button>
      </div>
    </div>
  </div>
</template>
